import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";

const SettingsContext = createContext(null);

const FALLBACK = {
  // brand & copy
  site_name: "PeptideSeries",
  tagline: "Doctor-verified peptide platform · est. 2026",
  hero_headline_1: "Clinical-grade peptides,",
  hero_headline_2: "dispensed under",
  hero_headline_accent: "verified",
  hero_headline_3: "protocols.",
  hero_subtitle:
    "Reserved for licensed physicians. Every account is verified through ID and Exequatur or Colegiatura review by the platform administrator before pricing and ordering are unlocked.",
  about_intro:
    "PeptideSeries exists to bring pharmaceutical-grade peptides into a regulated, transparent distribution path. We do not sell to patients. Every doctor account is verified by ID and medical license (Exequatur / Colegiatura) before access is granted. Every batch is traceable.",
  // contact
  contact_email: "support@peptideseries.com",
  contact_phone: "+1 (555) 010-7890",
  address: "",
  social_instagram: "",
  social_twitter: "",
  social_facebook: "",
  footer_note:
    "A doctor-only peptide platform: ID + medical license required for ordering.",
  // stats
  stat_active_count: "23",
  stat_active_label: "active peptides",
  stat_lab_tested: "100%",
  stat_lab_tested_label: "lab-tested batches",
  rx_review_sla: "48h",
  shipping_zones: "EU/US",
  // design tokens
  accent_color: "#0D9488",
  primary_color: "#0F172A",
  logo_url: "",
  hero_image_url:
    "https://images.unsplash.com/photo-1764437358284-4721473936f8?w=1800&q=80",
  about_image_url:
    "https://images.unsplash.com/photo-1669101283985-ab2b293923df?w=1400&q=85",
  category_antiaging_image:
    "https://images.unsplash.com/photo-1764437358284-4721473936f8?w=900&q=85",
  category_weightloss_image:
    "https://images.unsplash.com/photo-1642425777032-0b2249756797?w=900&q=85",
  category_fitness_image:
    "https://images.unsplash.com/photo-1669101283985-ab2b293923df?w=900&q=85",
  // maintenance (admin-only fields will be empty for public)
  maintenance_mode: "off",
  maintenance_title: "We'll be right back",
  maintenance_message:
    "We are performing scheduled maintenance. Please check back shortly.",
  // patient consent
  consent_title: "Patient Informed Consent & Service Agreement",
  consent_body: "",
  consent_recipient_email: "info@peptideseries.com",
  consent_sender_email: "",
};

function hexToHsl(hex) {
  if (!hex || typeof hex !== "string") return null;
  const m = hex.trim().replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(m)) return null;
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(FALLBACK);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings({ ...FALLBACK, ...data });
    } catch {
      setSettings(FALLBACK);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Apply design tokens (colors) live as CSS variables
  useEffect(() => {
    const root = document.documentElement;
    const accentHsl = hexToHsl(settings.accent_color);
    const primaryHsl = hexToHsl(settings.primary_color);
    if (accentHsl) root.style.setProperty("--accent", accentHsl);
    if (primaryHsl) {
      root.style.setProperty("--primary", primaryHsl);
      root.style.setProperty("--foreground", primaryHsl);
      root.style.setProperty("--ring", primaryHsl);
    }
  }, [settings.accent_color, settings.primary_color]);

  return (
    <SettingsContext.Provider value={useMemo(() => ({ settings, refresh, loaded }), [settings, refresh, loaded])}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be inside SettingsProvider");
  return ctx;
}

export const SETTINGS_FALLBACK = FALLBACK;
